import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { sharedStyles } from '../styles/shared-styles';
import { DebugUiBridge } from '../DebugUiBridge';
import type { DebugHudSnapshot } from '../types';

/**
 * Debug Controls Panel
 * 
 * Standalone Lit component for core debug toggles and settings.
 * Includes debug rendering, player pill, spline debug, weapon mount, and layout reset.
 */
@customElement('debug-controls-panel')
export class DebugControlsPanel extends LitElement {
  @property({ type: Boolean }) debugRenderingEnabled = false;
  @property({ type: Boolean }) splineDebugVisible = false;
  @property({ type: Boolean }) playerPillVisible = false;
  @property({ type: Boolean }) weaponMountVisible = false;
  @property({ type: Boolean }) collisionDebugVisible = false;
  @property({ type: Boolean }) lightDebugVisible = false;
  
  @state() private snapshot: DebugHudSnapshot | null = null;
  @state() private splineStatus: { found: boolean; source?: string } | null = null;
  @state() private cullingEnabled = true;
  @state() private cullDistance = 30;
  @state() private frustumFarPlane = 400;
  
  static styles = [
    sharedStyles,
    css`
    :host {
      display: block;
      color: white;
      font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      padding: 16px;
    }
    
    .control-group {
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .control-group:last-of-type {
      border-bottom: none;
    }
    
    h3 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      opacity: 0.95;
      color: rgba(255, 255, 255, 0.9);
    }
    
    label {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 0;
      cursor: pointer;
      user-select: none;
    }
    
    label:hover {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
      padding-left: 4px;
    }
    
    input[type="checkbox"] {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
    
    .status-row {
      display: flex;
      gap: 10px;
      align-items: center;
      margin: 8px 0;
      font-size: 12px;
    }
    
    .status-row .label {
      min-width: 80px;
      opacity: 0.75;
    }
    
    .status-row .value {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
      opacity: 0.95;
    }
    
    .pill.on {
      border-color: rgba(124, 252, 144, 0.4);
      background: rgba(124, 252, 144, 0.1);
      color: #7CFC90;
    }
    
    .pill.off {
      opacity: 0.6;
    }
    
    .warning {
      color: #ff8080;
      font-family: monospace;
      font-size: 11px;
      margin: 8px 0;
    }
    
    .actions {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .hotkey {
      opacity: 0.5;
      font-size: 10px;
      margin-left: 4px;
    }
    
    .slider-row {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 8px 0;
    }
    
    .slider-row .label {
      min-width: 100px;
      opacity: 0.75;
      font-size: 12px;
    }
    
    .slider-row input[type="range"] {
      flex: 1;
      height: 4px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
      appearance: none;
      cursor: pointer;
    }
    
    .slider-row input[type="range"]::-webkit-slider-thumb {
      appearance: none;
      width: 14px;
      height: 14px;
      background: #7CFC90;
      border-radius: 50%;
      cursor: pointer;
    }
    
    .slider-row .value {
      min-width: 40px;
      text-align: right;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
    }
  `];
  
  connectedCallback() {
    super.connectedCallback();
    // Listen for debug UI events
    window.addEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
  }
  
  private onDebugEvent = (e: CustomEvent) => {
    const event = e.detail;
    
    switch (event.type) {
      case 'snapshot':
        this.snapshot = event.snapshot;
        // Sync culling state from snapshot
        if (event.snapshot.culling) {
          this.cullingEnabled = event.snapshot.culling.enabled;
          this.cullDistance = event.snapshot.culling.cullDistance;
          this.frustumFarPlane = event.snapshot.culling.frustumFarPlane ?? 50;
        }
        break;
      
      case 'splineStatus':
        this.splineStatus = event.status;
        break;
      
      case 'debugRendering:changed':
        this.debugRenderingEnabled = event.enabled;
        break;
    }
  };
  
  private toggleDebugRendering() {
    DebugUiBridge.dispatchCommand({
      type: 'debugRendering:set',
      enabled: !this.debugRenderingEnabled
    });
  }
  
  private toggleSplineDebug() {
    this.splineDebugVisible = !this.splineDebugVisible;
    DebugUiBridge.dispatchCommand({
      type: 'splineDebug:set',
      enabled: this.splineDebugVisible
    });
  }
  
  private togglePlayerPill() {
    this.playerPillVisible = !this.playerPillVisible;
    DebugUiBridge.dispatchCommand({
      type: 'playerDebug:set',
      enabled: this.playerPillVisible
    });
  }
  
  private toggleWeaponMount() {
    this.weaponMountVisible = !this.weaponMountVisible;
    DebugUiBridge.dispatchCommand({
      type: 'weaponMountDebug:set',
      enabled: this.weaponMountVisible
    });
  }

  private toggleCollisionDebug() {
    this.collisionDebugVisible = !this.collisionDebugVisible;
    DebugUiBridge.dispatchCommand({
      type: 'collisionDebug:set',
      enabled: this.collisionDebugVisible
    });
  }

  private toggleLightDebug() {
    this.lightDebugVisible = !this.lightDebugVisible;
    DebugUiBridge.dispatchCommand({
      type: 'lightDebug:set',
      enabled: this.lightDebugVisible
    });
  }

  private toggleCulling() {
    this.cullingEnabled = !this.cullingEnabled;
    DebugUiBridge.dispatchCommand({
      type: 'culling:set',
      enabled: this.cullingEnabled
    });
  }

  private onCullDistanceChange(e: Event) {
    const value = parseInt((e.target as HTMLInputElement).value, 10);
    this.cullDistance = value;
    DebugUiBridge.dispatchCommand({
      type: 'culling:distance:set',
      value
    });
  }

  private onFrustumFarPlaneChange(e: Event) {
    const value = parseInt((e.target as HTMLInputElement).value, 10);
    this.frustumFarPlane = value;
    DebugUiBridge.dispatchCommand({
      type: 'culling:frustum:set',
      value
    });
  }

  private resetLayout() {
    if (confirm('Reset all panels to default layout? This will clear all settings and positions.')) {
      DebugUiBridge.dispatchCommand({ type: 'hud:reset' });
    }
  }
  
  render() {
    const s = this.snapshot;
    
    return html`
      <div class="control-group">
        <h3>Debug Rendering</h3>
        <div class="status-row">
          <span class="pill ${this.debugRenderingEnabled ? 'on' : 'off'}">
            ${this.debugRenderingEnabled ? 'ON' : 'OFF'}
          </span>
          <button @click=${this.toggleDebugRendering}>
            Toggle <span class="hotkey">(. key)</span>
          </button>
        </div>
      </div>
      
      <div class="control-group">
        <h3>Player State</h3>
        <div class="status-row">
          <span class="label">State:</span>
          <span class="value">${s ? `${s.state} (Anim: ${s.animationState})` : '—'}</span>
        </div>
        <div class="status-row">
          <span class="label">Camera:</span>
          <span class="value">${s ? s.cameraMode : '—'}</span>
        </div>
        <div class="status-row">
          <span class="label">Velocity Y:</span>
          <span class="value">${s ? s.velocityY.toFixed(2) : '—'}</span>
        </div>
        <div class="status-row">
          <span class="label">Position:</span>
          <span class="value">
            ${s ? `(${s.position.x.toFixed(1)}, ${s.position.y.toFixed(1)}, ${s.position.z.toFixed(1)})` : '—'}
          </span>
        </div>
        <div class="status-row">
          <span class="label">Grounded:</span>
          <span class="value">${s ? (s.grounded ? '✓' : '✗') : '—'}</span>
        </div>
        <div class="status-row">
          <span class="label">Spline:</span>
          <span class="value">
            ${this.splineStatus
              ? this.splineStatus.found
                ? `✓ Custom (${this.splineStatus.source ?? 'unknown'})`
                : '⚠ Default'
              : '—'}
          </span>
        </div>
      </div>
      
      <div class="control-group">
        <h3>Debug Geometry</h3>
        <label>
          <input
            type="checkbox"
            ?checked=${this.splineDebugVisible}
            @change=${this.toggleSplineDebug}
          />
          Show Spline Path
        </label>
        <label>
          <input
            type="checkbox"
            ?checked=${this.playerPillVisible}
            @change=${this.togglePlayerPill}
          />
          Show Player Pill
        </label>
        <label>
          <input
            type="checkbox"
            ?checked=${this.weaponMountVisible}
            @change=${this.toggleWeaponMount}
          />
          Show Weapon Mount
        </label>
        <label>
          <input
            type="checkbox"
            ?checked=${this.collisionDebugVisible}
            @change=${this.toggleCollisionDebug}
          />
          Show Collision Geometry
        </label>
        <label>
          <input
            type="checkbox"
            ?checked=${this.lightDebugVisible}
            @change=${this.toggleLightDebug}
          />
          Show Light Sources
        </label>
      </div>
      
      <div class="control-group">
        <h3>Spatial Culling</h3>
        <label>
          <input
            type="checkbox"
            ?checked=${this.cullingEnabled}
            @change=${this.toggleCulling}
          />
          Enable Distance Culling
        </label>
        <div class="slider-row">
          <span class="label">Cull Distance:</span>
          <input
            type="range"
            min="5"
            max="100"
            .value=${String(this.cullDistance)}
            @input=${this.onCullDistanceChange}
            ?disabled=${!this.cullingEnabled}
          />
          <span class="value">${this.cullDistance}m</span>
        </div>
        <div class="slider-row">
          <span class="label">Frustum Far:</span>
          <input
            type="range"
            min="10"
            max="200"
            .value=${String(this.frustumFarPlane)}
            @input=${this.onFrustumFarPlaneChange}
            ?disabled=${!this.cullingEnabled}
          />
          <span class="value">${this.frustumFarPlane}m</span>
        </div>
      </div>
      
      ${s?.missingAnimations?.length
        ? html`<div class="warning">⚠ Missing Animations: ${s.missingAnimations.join(', ')}</div>`
        : nothing}
      
      ${s?.animationDebugInfo
        ? html`<div class="status-row"><div class="value">${s.animationDebugInfo}</div></div>`
        : nothing}
      
      <div class="actions">
        <button @click=${this.resetLayout}>Reset HUD Layout + Options</button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'debug-controls-panel': DebugControlsPanel;
  }
}
